import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Zap,
  TrendingUp,
  Users,
  Building2,
  Clock3,
  ChevronRight,
  PlayCircle,
  Star,
  Sparkles,
  BarChart3,
  Lock,
} from "lucide-react";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  EASE,
} from "../components/motion/ScrollFX";

// Same theme tokens as Work.jsx / Careers.jsx / Solutions.jsx / Contact.jsx.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// Hero background — "orbital system" motif: concentric elliptical rings
// rotate at different speeds with satellite nodes traveling around
// them, suggesting solutions orbiting a central business core.
// ---------------------------------------------------------------------
function OrbitalField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <DotGrid light />
      <span className="orbital-glow" />
      <span className="orbital-ellipse orbital-ellipse-1">
        <span className="orbital-node" />
      </span>
      <span className="orbital-ellipse orbital-ellipse-2">
        <span className="orbital-node orbital-node-gold" />
      </span>
      <span className="orbital-ellipse orbital-ellipse-3">
        <span className="orbital-node" />
      </span>
      <span className="orbital-core" />
    </div>
  );
}

export default function SolutionPageTemplate({
  title,
  subtitle,
  image,
  challenges = [],
  solutions = [],
  features = [],
  benefits = [],
  industries = [],
  faqs = [],
}) {
  const [openFAQ, setOpenFAQ] = useState(0);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .orbital-glow {
          position: absolute; top: 34%; left: 50%; width: 34rem; height: 34rem;
          margin: -17rem 0 0 -17rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}30, transparent 68%);
          filter: blur(70px);
          animation: orbitalGlowBreathe 7s ease-in-out infinite;
        }
        @keyframes orbitalGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        .orbital-core {
          position: absolute; top: 34%; left: 50%; width: 10px; height: 10px;
          margin: -5px 0 0 -5px; border-radius: 9999px;
          background: ${theme.accent};
          box-shadow: 0 0 16px 4px ${theme.accent}aa;
        }
        .orbital-ellipse {
          position: absolute; top: 34%; left: 50%;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 9999px;
        }
        .orbital-ellipse-1 {
          width: 26rem; height: 12rem; margin: -6rem 0 0 -13rem;
          animation: orbitalSpin1 22s linear infinite;
        }
        .orbital-ellipse-2 {
          width: 38rem; height: 17rem; margin: -8.5rem 0 0 -19rem;
          border-color: rgba(217,164,65,0.18);
          animation: orbitalSpin2 32s linear infinite reverse;
        }
        .orbital-ellipse-3 {
          width: 50rem; height: 22rem; margin: -11rem 0 0 -25rem;
          animation: orbitalSpin3 44s linear infinite;
        }
        @keyframes orbitalSpin1 { from { transform: rotate(-8deg); } to { transform: rotate(352deg); } }
        @keyframes orbitalSpin2 { from { transform: rotate(6deg); } to { transform: rotate(-354deg); } }
        @keyframes orbitalSpin3 { from { transform: rotate(-4deg); } to { transform: rotate(356deg); } }
        .orbital-node {
          position: absolute; top: -4px; left: 50%; width: 8px; height: 8px;
          margin-left: -4px; border-radius: 9999px;
          background: ${theme.accent};
          box-shadow: 0 0 10px 3px ${theme.accent}88;
        }
        .orbital-node-gold { background: ${theme.gold}; box-shadow: 0 0 10px 3px ${theme.gold}88; }

        @media (prefers-reduced-motion: reduce) {
          .orbital-glow, .orbital-ellipse-1, .orbital-ellipse-2, .orbital-ellipse-3 { animation: none !important; }
        }

        .card-glow-light {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow-light { opacity: 0.18; }

        .card-glow-dark {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.1; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow-dark { opacity: 0.22; }
      `}</style>

      {/* ================= HERO (dark, orbital field) ================= */}
      <section className="relative pt-40 pb-28 md:pt-48 md:pb-32 px-6 overflow-hidden" style={{ backgroundColor: theme.heroBg }}>
        <div className="absolute inset-0">
          <OrbitalField />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          {/* LEFT */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium border"
              style={{ borderColor: `${theme.accent}4D`, backgroundColor: `${theme.accent}1A`, color: "#FF8A93" }}
            >
              <Sparkles size={16} />
              Enterprise Digital Solutions
            </div>

            <h1 className="mt-8 text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-white">
              <SplitText text={title} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: EASE }}
              className="mt-8 text-white/60 text-lg leading-9 max-w-xl"
            >
              {subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="flex flex-wrap gap-5 mt-10"
            >
              <MagneticButton>
                <button
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group hover:brightness-110 transition-all px-8 py-4 rounded-xl font-semibold flex items-center gap-3 text-white"
                >
                  Book Demo
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
                </button>
              </MagneticButton>

              <MagneticButton strength={0.25}>
                <button
                  data-cursor="hover"
                  className="border border-white/15 hover:bg-white hover:text-[#221619] transition-colors px-8 py-4 rounded-xl font-semibold flex items-center gap-3 text-white"
                >
                  <PlayCircle size={18} />
                  Learn More
                </button>
              </MagneticButton>
            </motion.div>

            {/* Trust */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="grid grid-cols-3 gap-5 mt-16"
            >
              {[
                { icon: ShieldCheck, value: "99.99%", label: "Platform Uptime" },
                { icon: TrendingUp, value: "3X", label: "Faster Processing" },
                { icon: Users, value: "500+", label: "Businesses" },
              ].map((stat, i) => (
                <div key={i} className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <stat.icon className="mb-3" style={{ color: theme.accent }} />
                  <h3 className="font-bold text-2xl text-white">{stat.value}</h3>
                  <p className="text-white/45 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* RIGHT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-[40px] blur-[100px] opacity-20" style={{ backgroundColor: theme.accent }} />

            <img src={image} alt={title} className="relative rounded-[40px] border border-white/10 shadow-2xl" />

            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="absolute -left-8 top-10 backdrop-blur-xl rounded-2xl p-5 shadow-2xl"
              style={{ backgroundColor: "rgba(16,16,16,0.9)", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.accent}1A` }}>
                  <BarChart3 style={{ color: theme.accent }} />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Real-Time Analytics</h4>
                  <p className="text-sm text-white/45 mt-1">Live transaction monitoring</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 5 }}
              className="absolute -right-8 bottom-10 backdrop-blur-xl rounded-2xl p-5 shadow-2xl"
              style={{ backgroundColor: "rgba(16,16,16,0.9)", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.accent}1A` }}>
                  <Lock style={{ color: theme.accent }} />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Enterprise Security</h4>
                  <p className="text-sm text-white/45 mt-1">PCI DSS compliant</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= CHALLENGES (light) ================= */}
      <section className="relative py-24 md:py-28 px-6" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-7xl mx-auto">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Challenges</Eyebrow>
            <h2 className="text-4xl lg:text-5xl font-bold" style={{ color: theme.ink }}>
              Industry <span style={{ color: theme.accent }}>Challenges</span>
            </h2>
            <p className="mt-6 text-lg" style={{ color: theme.inkMuted }}>
              Businesses today require secure, scalable and intelligent
              financial infrastructure to remain competitive in the digital era.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 xl:grid-cols-4 gap-8"
          >
            {challenges.map((item, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ y: -10, transition: { duration: 0.25 } }}
                className="group relative overflow-hidden rounded-3xl p-8"
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
              >
                <span className="card-glow-light" />
                <div className="relative">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 transition-all"
                    style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                  >
                    <ShieldCheck />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4" style={{ color: theme.ink }}>{item.title}</h3>
                  <p className="leading-8" style={{ color: theme.inkMuted }}>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= WHY CHOOSE US (dark) ================= */}
      <section className="relative py-24 md:py-28 px-6 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <RevealSection className="relative">
            <div className="absolute inset-0 blur-[120px] opacity-20 rounded-full" style={{ backgroundColor: theme.accent }} />
            <img src={image} alt={title} className="relative rounded-[32px] border border-white/10" />
          </RevealSection>

          <RevealSection delay={0.15}>
            <Eyebrow light>Why Choose Us</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight text-white">
              Built for Modern <span style={{ color: theme.accent }}>Digital Businesses</span>
            </h2>
            <p className="mt-8 text-lg leading-9 text-white/60">
              We empower businesses with enterprise-grade payment
              infrastructure, secure APIs, intelligent automation and scalable
              fintech solutions that accelerate growth.
            </p>

            <div className="space-y-6 mt-12">
              {[
                "Enterprise-grade security",
                "Real-time transaction monitoring",
                "Instant settlements",
                "Scalable cloud infrastructure",
                "Developer-friendly APIs",
                "24×7 dedicated support",
              ].map((point, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: theme.accent }}>
                    <CheckCircle2 size={18} className="text-white" />
                  </div>
                  <p className="text-lg text-white">{point}</p>
                </div>
              ))}
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ================= FEATURES (light) ================= */}
      <section className="relative py-24 md:py-28 px-6" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-7xl mx-auto">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Capabilities</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              Core <span style={{ color: theme.accent }}>Features</span>
            </h2>
            <p className="mt-6" style={{ color: theme.inkMuted }}>
              Everything your business needs to simplify payments,
              automate financial operations and deliver exceptional customer
              experiences.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ y: -10 }}
                className="group relative rounded-3xl p-8 overflow-hidden"
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
              >
                <span className="card-glow-light" />
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8" style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}>
                    <Zap />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4" style={{ color: theme.ink }}>{feature.title}</h3>
                  <p className="leading-8" style={{ color: theme.inkMuted }}>{feature.description}</p>
                  <button
                    className="mt-8 flex items-center gap-2 font-semibold group-hover:gap-4 transition-all"
                    style={{ color: theme.accent }}
                  >
                    Learn More
                    <ChevronRight size={18} />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= BENEFITS (dark) ================= */}
      <section className="relative py-24 md:py-28 px-6 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-7xl mx-auto">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center light>Business Benefits</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Why Businesses <span style={{ color: theme.accent }}>Trust Us</span>
            </h2>
            <p className="max-w-3xl mx-auto mt-6 text-lg text-white/60">
              From startups to large enterprises, our infrastructure is built
              to maximize efficiency, security and business growth.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-16"
          >
            {[
              { icon: TrendingUp, value: "99.99%", label: "Platform Uptime" },
              { icon: Zap, value: "3X", label: "Faster Processing" },
              { icon: ShieldCheck, value: "100%", label: "Secure Infrastructure" },
              { icon: Clock3, value: "24/7", label: "Expert Support" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                whileHover={{ y: -8 }}
                className="rounded-3xl p-8"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <stat.icon className="mb-6" size={40} style={{ color: theme.accent }} />
                <h3 className="text-5xl font-bold text-white">{stat.value}</h3>
                <p className="text-white/50 mt-3">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid lg:grid-cols-2 gap-8"
          >
            {benefits.map((item, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ x: 8 }}
                className="rounded-3xl p-8 flex gap-6"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: theme.accent }}>
                  <CheckCircle2 className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-white">{item.title}</h3>
                  <p className="mt-4 leading-8 text-white/55">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= HOW IT WORKS (light) ================= */}
      <section className="relative py-24 md:py-28 px-6" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto">
          <RevealSection className="text-center mb-20">
            <Eyebrow center>Process</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              How It <span style={{ color: theme.accent }}>Works</span>
            </h2>
          </RevealSection>

          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-[2px]" style={{ backgroundColor: `${theme.accent}4D` }} />

            {[
              { title: "Consultation", desc: "Understand your business requirements and identify the ideal fintech solution." },
              { title: "Integration", desc: "Quick API integration with comprehensive technical documentation." },
              { title: "Testing", desc: "Validate security, performance and compliance before deployment." },
              { title: "Go Live", desc: "Launch confidently with monitoring, analytics and dedicated support." },
            ].map((step, index) => (
              <RevealSection key={index} className="relative flex gap-8 mb-14">
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold shrink-0 text-white" style={{ backgroundColor: theme.accent }}>
                  {index + 1}
                </div>
                <div className="rounded-3xl p-8 flex-1" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                  <h3 className="text-2xl font-semibold" style={{ color: theme.ink }}>{step.title}</h3>
                  <p className="mt-4 leading-8" style={{ color: theme.inkMuted }}>{step.desc}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRIES (dark) ================= */}
      <section className="relative py-24 md:py-28 px-6 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-7xl mx-auto">
          <RevealSection className="text-center mb-16">
            <Eyebrow center light>Industries</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Businesses We <span style={{ color: theme.accent }}>Empower</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {industries.map((item, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ y: -8, scale: 1.03 }}
                className="rounded-3xl p-8 text-center"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <Building2 size={42} className="mx-auto mb-6" style={{ color: theme.accent }} />
                <h3 className="text-2xl font-semibold text-white">{item}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= FAQ (light) ================= */}
      <section className="relative py-24 md:py-28 px-6" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-5xl mx-auto">
          <RevealSection className="text-center mb-16">
            <Eyebrow center>FAQs</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              Frequently Asked <span style={{ color: theme.accent }}>Questions</span>
            </h2>
            <p className="mt-6" style={{ color: theme.inkMuted }}>
              Everything you need to know before getting started.
            </p>
          </RevealSection>

          <div className="space-y-5">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                layout
                className="rounded-3xl overflow-hidden"
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  data-cursor="hover"
                  className="w-full flex justify-between items-center p-7 text-left"
                >
                  <span className="text-xl font-semibold" style={{ color: theme.ink }}>{faq.question}</span>
                  <ChevronRight
                    className={`transition-transform ${openFAQ === index ? "rotate-90" : ""}`}
                    style={{ color: theme.accent }}
                  />
                </button>

                {openFAQ === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-7 pb-7"
                  >
                    <p className="leading-8" style={{ color: theme.inkMuted }}>{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section className="relative pb-32 pt-8 px-6" style={{ backgroundColor: theme.surface }}>
        <RevealSection className="max-w-7xl mx-auto rounded-[40px] overflow-hidden relative">
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accentDeep} 100%)` }}
          />
          <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-white/10 blur-[120px]" />

          <div className="relative px-12 py-20 lg:px-20 flex flex-col lg:flex-row justify-between items-center gap-12">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm text-white">
                <Star size={16} />
                Let's Build Together
              </span>

              <h2 className="text-4xl lg:text-6xl font-bold mt-6 leading-tight text-white">
                Ready to Transform
                <br />
                Your Business?
              </h2>

              <p className="mt-6 text-white/85 text-lg max-w-2xl leading-8">
                Empower your organization with secure APIs,
                enterprise-grade payment infrastructure,
                automation tools and intelligent fintech solutions.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-5">
              <MagneticButton>
                <button className="bg-black hover:bg-[#111] transition px-8 py-4 rounded-xl font-semibold flex items-center gap-3 text-white">
                  Book a Demo
                  <ArrowRight size={18} />
                </button>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <button className="border border-white/30 hover:bg-white hover:text-black transition px-8 py-4 rounded-xl font-semibold text-white">
                  Contact Sales
                </button>
              </MagneticButton>
            </div>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}